import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/esm/Table";
import Checkbox from "./Checkbox";
import "./SelectClassStudents.css";

function SelectClassStudents(props) {
  const {
    classStudents,
    selectStudentId,
    setSelectStudentId,
    selectAssessmentId,
    gradeLevel1,
    gradeLevel2,
    gradeLevel3,
    gradeLevel4,
    availableAssessments,
  } = props;

  const [completedAssessmentIcons, setCompletedAssessmentIcons] = useState({});
  const [fetchedAssessmentIds, setFetchedAssessmentIds] = useState({});

  const fetchCompletedAssessmentIcons = () => {
    const icons = {};
    const idsMap = {};

    // Build a map of assessmentid -> grade level booleans from available assessments
    const assessmentGradeMap = {};
    availableAssessments?.forEach(assessment => {
      const id = String(assessment?.assessmentid ?? assessment?.id ?? "");
      if (id) {
        assessmentGradeMap[id] = {
          grade_level1: assessment.grade_level1,
          grade_level2: assessment.grade_level2,
          grade_level3: assessment.grade_level3,
          grade_level4: assessment.grade_level4,
        };
      }
    });

    classStudents?.forEach((student) => {
      if (student.assessments) {
        // Filter assessments dynamically based on grade level match
        const filteredAssessments = student.assessments.filter(
          (assessment) => {
            const isCompleted = assessment.completed && !assessment.assessment?.task;
            if (!isCompleted) return false;

            // Get the grade level booleans for this assessment
            const assessmentGrades = assessmentGradeMap[assessment.assessmentid];
            if (!assessmentGrades) return false;
            
            // Match against class grade levels
            if (gradeLevel1 && assessmentGrades.grade_level1) return true;
            if (gradeLevel2 && assessmentGrades.grade_level2) return true;
            if (gradeLevel3 && assessmentGrades.grade_level3) return true;
            if (gradeLevel4 && assessmentGrades.grade_level4) return true;
            
            return false;
          }
        );

        // Map the filtered assessments to icons
        icons[student.id] = filteredAssessments
          .map((assessment) => ({
            name: assessment.name,
            icon: getAssessmentIconName(assessment.name),
            assessment: assessment,
          }))
          .sort(
            (a, b) => getAssessmentOrder(a.name) - getAssessmentOrder(b.name)
          );

        // Store the assessment IDs for later use
        idsMap[student.id] = filteredAssessments.map(
          (assessment) => assessment.assessmentid
        );
      } else {
        icons[student.id] = [];
        idsMap[student.id] = [];
      }
    });

    setFetchedAssessmentIds(idsMap);
    setCompletedAssessmentIcons(icons);
  };

  console.log("fetchedAssessmentIds:", fetchedAssessmentIds);
  console.log("completedAssessmentIcons:", completedAssessmentIcons);
  console.log("classStudents:", classStudents);
  console.log("selectAssessmentId:", selectAssessmentId);
  console.log("availableAssessments:", availableAssessments);

  useEffect(() => {
    fetchCompletedAssessmentIcons();
  }, [classStudents, availableAssessments]);

  const getAssessmentIconName = (assessmentName) => {
    assessmentName = assessmentName.toLowerCase();
    if (
      assessmentName.includes("baseline") ||
      assessmentName.includes("point 1") ||
      assessmentName.includes("autumn")
    ) {
      return "autumn";
    } else if (
      assessmentName.includes("midpoint") ||
      assessmentName.includes("point 2") ||
      assessmentName.includes("spring")
    ) {
      return "spring";
    } else if (
      assessmentName.includes("end of year") ||
      assessmentName.includes("point 3") ||
      assessmentName.includes("summer")
    ) {
      return "summer";
    } else {
      const nameParts = assessmentName
        .split(" ")
        .filter((part) => part !== "instance");
      console.log(nameParts.slice(0, 2).join("").toLowerCase());
      return nameParts.slice(0, 2).join("").toLowerCase();
    }
  };

  const getAssessmentOrder = (assessmentName) => {
    assessmentName = assessmentName.toLowerCase();
    if (
      assessmentName.includes("baseline") ||
      assessmentName.includes("point 1") ||
      assessmentName.includes("autumn")
    ) {
      return 1;
    } else if (
      assessmentName.includes("midpoint") ||
      assessmentName.includes("point 2") ||
      assessmentName.includes("spring")
    ) {
      return 2;
    } else if (
      assessmentName.includes("end of year") ||
      assessmentName.includes("point 3") ||
      assessmentName.includes("summer")
    ) {
      return 3;
    } else {
      return 4;
    }
  };

  const [isCheckAll, setIsCheckAll] = useState(false);

  const handleClick = (e) => {
    const { id, checked } = e.target;
    setSelectStudentId((prevIds) => {
      if (checked) {
        return [...prevIds, id];
      } else {
        return prevIds.filter((item) => item !== id);
      }
    });
  };

  const handleSelectAll = () => {
    setIsCheckAll((prevIsCheckAll) => {
      setSelectStudentId((prevIds) => {
        // Filter students who are eligible for screener assignment
        const eligibleStudents = classStudents.filter(
          (classStudent) =>
            !classStudent.disabled && // Not disabled
            !(classStudent.assessments && classStudent.assessments.some(assessment => !assessment.completed)) && // No pending assessments
            !fetchedAssessmentIds[classStudent.id]?.includes(selectAssessmentId) // Has not already been assigned this screener
        ).map(classStudent => classStudent.id);
  
        // If we are selecting all, merge the existing selected IDs with the eligible students
        if (!prevIsCheckAll) {
          return [...new Set([...prevIds, ...eligibleStudents])];
        } else {
          // If we are deselecting all, remove all eligible students from the selected IDs
          return prevIds.filter(id => !eligibleStudents.includes(id));
        }
      });
  
      return !prevIsCheckAll; // Toggle the isCheckAll state
    });
  };

  return (
    <Row>
      <Col>
        {classStudents?.length > 0 ? (
          <div>
            <h3 className="select-student-header">
              Class Students ({classStudents?.length})
            </h3>
            <p>Tick "Select all" to select all students for this screener.</p>
            <Table className="custom-table">
              <thead className="table-header">
                <tr>
                  <th>Name</th>
                  <th>Completed Screeners</th>
                  <th>Current</th>
                  <th>
                    <div className="select-all">
                      {classStudents.some(
                        (student) =>
                          !student.disabled &&
                          !(
                            student.assessments &&
                            student.assessments.some(
                              (assessment) => !assessment.completed
                            )
                          )
                      ) ? (
                        <>
                          <Checkbox
                            type="checkbox"
                            name="selectAll"
                            id="selectAll"
                            handleClick={handleSelectAll}
                            isChecked={isCheckAll}
                          />
                          Select All
                        </>
                      ) : (
                        <span>All have screeners assigned</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {classStudents.map((classStudent) => {
                  const hasAssessmentMatch =
                    fetchedAssessmentIds[classStudent.id]?.includes(
                      selectAssessmentId
                    );
                  const hasIncompleteAssessment =
                    classStudent.assessments?.some(
                      (assessment) => !assessment.completed
                    );

                  return (
                    <tr key={classStudent.id} className="table-row">
                      <td className="table-data">{classStudent.name}</td>
                      <td>
                        {completedAssessmentIcons[classStudent.id]?.map(
                          (assessment, index) => (
                            <span key={index} className="assessment-assigned">
                              <img
                                src={`/assets/imgs/${assessment.icon}-icon.png`}
                                alt={`${assessment.name} Icon`}
                                className="season-assessment-icon-small"
                              />
                            </span>
                          )
                        )}
                      </td>
                      <td>
                        {classStudent.assessments &&
                          classStudent.assessments.some(
                            (assessment) => !assessment.completed
                          ) ? (
                          classStudent.assessments.map((assessment, index) =>
                            !assessment.completed ? (
                              <span key={index} className="assessment-assigned">
                                <img
                                  src={`/assets/imgs/${getAssessmentIconName(assessment.name) ||
                                    assessment.name.split(" ")[0].toLowerCase()
                                    }-icon.png`}
                                  alt={`${assessment.name} Icon`}
                                  className="season-assessment-icon-small"
                                />
                              </span>
                            ) : null
                          )
                        ) : (
                          <span>None</span>
                        )}
                      </td>
                      <td>
                        {classStudent.disabled ||
                          hasIncompleteAssessment ||
                          hasAssessmentMatch ? null : (
                          <Checkbox
                            key={classStudent.id}
                            type="checkbox"
                            name={classStudent.name}
                            id={classStudent.id}
                            handleClick={handleClick}
                            isChecked={selectStudentId.includes(
                              classStudent.id
                            )}
                            isDisabled={false}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="no-students-message">
            <h3>No Students have been added to your class yet.</h3>
            <p>
              To do this, please go to Classrooms and select the
              class you want to add students to. From here, select "Add Students"
              and add your students to your class. Thank you.
            </p>
          </div>
        )}
      </Col>
    </Row>
  );
}

export default SelectClassStudents;
